import { useState, useCallback } from "react";
import { existingUserBookingSchema } from "./Schema.js";

export function useBookingForm(schema = existingUserBookingSchema) {
  const [values, setValues] = useState({
    participants: 1,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** Validates a single field — call on every onChange */
  const validateField = useCallback(
    (field, value) => {
      const singleField = schema.pick({ [field]: true });
      const result = singleField.safeParse({ [field]: value });
      const errorMsg = !result.success
        ? result.error.issues[0]?.message
        : undefined;
      setErrors((prev) => ({ ...prev, [field]: errorMsg }));
      return !errorMsg;
    },
    [schema],
  );

  /** Wire to onChange on every field */
  const handleChange = useCallback(
    (field, rawValue) => {
      const value =
        field === "participants"
          ? rawValue === ""
            ? undefined
            : Number(rawValue)
          : rawValue;

      setValues((prev) => {
        let updated = { ...prev, [field]: value };

        // 🔥 dependency reset
        if (field === "cluster") {
          updated.venue = "";
          updated.sport = "";
          updated.zone = "";
          updated.timeSlot = "";
          updated.bookingDate = ""
        }

        if (field === "venue") {
          updated.sport = "";
          updated.zone = "";
          updated.timeSlot = "";
          updated.bookingDate = ""
        }

        if (field === "sport") {
          updated.zone = "";
          updated.timeSlot = "";
          updated.bookingDate = ""
        }

        if (field === "zone") {
          updated.timeSlot = "";
        }
        if (field === "state") {
          updated.district = "";
        }

        return updated;
      });

      setTouched((prev) => ({ ...prev, [field]: true }));
      validateField(field, value);
    },
    [validateField],
  );

  const validateAll = useCallback(() => {
    const result = schema.safeParse(values);

    if (!result.success) {
      const fieldErrors = {};

      result.error.issues.forEach((e) => {
        const key = e.path[0];
        if (key && !fieldErrors[key]) fieldErrors[key] = e.message;
      });

      setErrors(fieldErrors);
      const allTouched = Object.keys(schema.shape).reduce(
        (acc, k) => ({ ...acc, [k]: true }),
        {},
      );
      setTouched(allTouched);
      return null;
    }

    setErrors({});
    return result.data;
  }, [schema, values]);

  const resetForm = useCallback(() => {
    setValues({});
    setErrors({});
    setTouched({});
  }, []);

  const handleSubmit = useCallback(
    (onSuccess = async () => {}) =>
      async (event) => {
        event?.preventDefault();

        const data = validateAll();
        if (!data) return;

        setIsSubmitting(true);
        try {
          await onSuccess(data);
        } finally {
          setIsSubmitting(false);
        }
      },
    [validateAll],
  );

  return {
    values,
    setValues,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleSubmit,
    resetForm,
  };
}
